import React, {useMemo} from 'react';
import {Table} from 'reactstrap';
import {useIntl} from 'react-intl';
import Button from '../../components/button';
import I18nMessage from '../../components/I18nMessage';
import LoadingBar from '../../components/loadingBar';
import {FieldSet} from '../../components/fieldset';
import {useDeviceFines} from '../../autobahn/features/certadoc/queries';
import './device.sass';

const CATEGORY_LABELS = {
    pagas: 'certadocCategoryPagas',
    aPagar: 'certadocCategoryAPagar',
    notificacoes: 'certadocCategoryNotificacoes',
    multasNic: 'certadocCategoryMultasNic',
    outros: 'certadocCategoryOutros',
};

const DeviceFines = ({data, cancel}) => {
    const intl = useIntl();
    const {data: fines, isLoading, error} = useDeviceFines(data && data.id);

    const rows = useMemo(() => {
        if (!fines) return [];
        return Object.keys(CATEGORY_LABELS).flatMap((category) =>
            (fines[category] || []).map((fine, index) => ({
                ...fine,
                category,
                key: `${category}-${index}`,
            })),
        );
    }, [fines]);

    const formatValue = (value) =>
        value == null ? '' : Number(value).toLocaleString('pt-BR', {minimumFractionDigits: 2});

    const renderBody = () => {
        if (isLoading) {
            return <LoadingBar />;
        }

        if (error) {
            // 400 is the server telling us the device has no usable plate.
            const message = error.status === 400 ? 'certadocNoPlate' : 'certadocRequestError';
            return <p className="text-center my-4"><I18nMessage id={message} /></p>;
        }

        if (!rows.length) {
            return <p className="text-center my-4"><I18nMessage id="certadocNoFines" /></p>;
        }

        return (
            <div style={{overflowX: 'auto'}}>
                <Table size="sm" striped responsive>
                    <thead>
                        <tr>
                            <th><I18nMessage id="certadocColCategory" /></th>
                            <th><I18nMessage id="certadocColAit" /></th>
                            <th><I18nMessage id="certadocColDate" /></th>
                            <th><I18nMessage id="certadocColTime" /></th>
                            <th><I18nMessage id="certadocColDescription" /></th>
                            <th><I18nMessage id="certadocColValue" /></th>
                            <th><I18nMessage id="certadocColSituation" /></th>
                            <th><I18nMessage id="certadocColStatus" /></th>
                            <th><I18nMessage id="certadocColAgency" /></th>
                            <th><I18nMessage id="certadocColCode" /></th>
                            <th><I18nMessage id="certadocColDueDate" /></th>
                            <th><I18nMessage id="certadocColDocuments" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((fine) => (
                            <tr key={fine.key}>
                                <td>{intl.formatMessage({id: CATEGORY_LABELS[fine.category]})}</td>
                                <td>{fine.ait}</td>
                                <td>{fine.dataMulta}</td>
                                <td>{fine.horaMulta}</td>
                                <td>{fine.descricao}</td>
                                <td>{formatValue(fine.valorMulta)}</td>
                                <td>{fine.situacao}</td>
                                <td>{fine.flagStatus}</td>
                                <td>{fine.tabelaOrigem}</td>
                                <td>{fine.codigoInfracao}</td>
                                <td>{fine.dataValidade}</td>
                                <td>
                                    {fine.boleto && (
                                        <a href={fine.boleto} target="_blank" rel="noopener noreferrer">
                                            {intl.formatMessage({id: 'certadocDocumentTicket'})}
                                        </a>
                                    )}
                                    {fine.boleto && fine.respaldoOrgao && ' · '}
                                    {fine.respaldoOrgao && (
                                        <a href={fine.respaldoOrgao} target="_blank" rel="noopener noreferrer">
                                            {intl.formatMessage({id: 'certadocDocumentSupport'})}
                                        </a>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <>
            <FieldSet
                className="text-left border p-4"
                title={`${intl.formatMessage({id: 'certadocFines'})} — ${data ? data.name : ''}`}>
                {renderBody()}
            </FieldSet>
            <div className={'rounded-group-button-form-bottom'}>
                <Button color={'warning'} className={'outline'} size={'lg'} onClick={cancel}>
                    <I18nMessage id="sharedBack" />
                </Button>
            </div>
        </>
    );
};

export default DeviceFines;
